import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Settings2, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { getAllConcepts } from '@/pages/payroll/Services/payroll.services';
// Se reutiliza el CRUD que ya existe en la página de nómina en vez de
// escribir uno nuevo: misma validación, mismo comportamiento.
import ConfigureConceptsModal from '@/pages/payroll/components/ConfigureConceptsModal';

const ConceptosPage = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getAllConcepts();
      const lista = [...(res?.data || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      setItems(lista);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const devengados = items.filter((c) => c.naturaleza === 'devengado');
  const deducciones = items.filter((c) => c.naturaleza === 'deduccion');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={() => setOpen(true)}>
            <Settings2 className="mr-2 h-4 w-4" />
            Administrar conceptos
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">Conceptos totales</p>
            <p className="text-2xl font-bold mt-1">{loading ? '—' : items.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-emerald-100">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="text-sm text-muted-foreground">Devengados</p>
            </div>
            <p className="text-2xl font-bold mt-1">{loading ? '—' : devengados.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-rose-100">
                <TrendingDown className="h-4 w-4 text-rose-600" />
              </div>
              <p className="text-sm text-muted-foreground">Deducciones</p>
            </div>
            <p className="text-2xl font-bold mt-1">{loading ? '—' : deducciones.length}</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Naturaleza</TableHead>
                <TableHead>Cálculo</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Base</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No hay conceptos configurados.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c) => (
                  <TableRow key={c._id} className={c.activo ? '' : 'opacity-50'}>
                    <TableCell className="font-mono text-xs">{c.codigo || '—'}</TableCell>
                    <TableCell className="font-medium">{c.nombre}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          c.naturaleza === 'devengado'
                            ? 'border-emerald-300 text-emerald-700'
                            : 'border-rose-300 text-rose-700'
                        }
                      >
                        {c.naturaleza === 'devengado' ? 'Devengado' : 'Deducción'}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">
                      {c.tipoCalculo}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.tipoCalculo === 'porcentaje'
                        ? `${Number(c.valor).toLocaleString('es-CO')}%`
                        : Number(c.valor).toLocaleString('es-CO', {
                            style: 'currency',
                            currency: 'COP',
                            maximumFractionDigits: 0,
                          })}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {c.baseCalculo === 'salario' ? 'Salario' : 'Total devengado'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ConfigureConceptsModal
        open={open}
        onOpenChange={setOpen}
        concepts={items}
        onChanged={cargar}
      />
    </motion.div>
  );
};

export default ConceptosPage;